import React, { createContext, useCallback, useContext, useLayoutEffect, useRef } from 'react';
import { Keyboard, PanResponder, Platform, StyleSheet, View } from 'react-native';
import type { GestureResponderEvent, PanResponderGestureState } from 'react-native';

// iOS's screen-edge shortcut supplements the visible Back button. Android keeps
// its system back gesture; web keeps browser navigation and text selection.
export const CHAT_BACK_EDGE = 24;
export const CHAT_BACK_DISTANCE = 72;
const BlockAncestorBack = createContext<(() => () => void) | null>(null);

// Native modals still belong to the React responder ancestry. Prevent a parent
// page from winning capture and navigating underneath an open modal.
export function useBlockAncestorEdgeBack(visible: boolean) {
  const block = useContext(BlockAncestorBack);
  useLayoutEffect(() => visible ? block?.() : undefined, [visible, block]);
}
export function isChatBackStart(g: PanResponderGestureState) {
  return g.numberActiveTouches === 1 && g.x0 >= 0 && g.x0 <= CHAT_BACK_EDGE;
}
export function isChatBackDirection(g: PanResponderGestureState) {
  return g.dx > 10 && g.dx > Math.abs(g.dy) * 1.5;
}

export default function ChatEdgeBack({ children, onBack, enabled = true, captureEdgeOnStart = false }: {
  children: React.ReactNode; onBack: (event: GestureResponderEvent) => void; enabled?: boolean;
  captureEdgeOnStart?: boolean;
}) {
  const blockParent = useContext(BlockAncestorBack);
  const blockers = useRef(0);
  const block = useCallback(() => {
    blockers.current += 1;
    const unblockParent = blockParent?.();
    return () => { blockers.current -= 1; unblockParent?.(); };
  }, [blockParent]);
  const latest = useRef({ onBack, enabled, captureEdgeOnStart });
  latest.current = { onBack, enabled, captureEdgeOnStart };
  const rejected = useRef(false);
  const startX = useRef<number | null>(null);
  const pan = useRef(PanResponder.create({
    // RN initializes g.x0 to zero before grant; it cannot identify the edge
    // during capture. Remember the actual first touch before children compete.
    onStartShouldSetPanResponderCapture: (event, g) => {
      startX.current = event.nativeEvent.pageX;
      rejected.current = g.numberActiveTouches !== 1;
      // RN Modal claims the initial responder and prevents its children from
      // negotiating later moves. Reserve only its narrow edge at touch-down.
      return latest.current.captureEdgeOnStart && latest.current.enabled &&
        blockers.current === 0 && isChatBackStart({ ...g, x0: startX.current });
    },
    onMoveShouldSetPanResponderCapture: (_event, g) => {
      if (!latest.current.enabled || blockers.current > 0 || startX.current === null ||
          !isChatBackStart({ ...g, x0: startX.current })) rejected.current = true;
      if (Math.max(Math.abs(g.dx), Math.abs(g.dy)) > 10 && !isChatBackDirection(g)) rejected.current = true;
      return !rejected.current && isChatBackDirection(g);
    },
    onPanResponderMove: (_event, g) => {
      if (g.numberActiveTouches !== 1) rejected.current = true;
      if (Math.max(Math.abs(g.dx), Math.abs(g.dy)) > 10 && !isChatBackDirection(g)) rejected.current = true;
    },
    onPanResponderRelease: (event, g) => {
      const complete = latest.current.enabled && blockers.current === 0 && !rejected.current &&
        g.dx >= CHAT_BACK_DISTANCE && isChatBackDirection(g);
      rejected.current = true;
      if (complete) { Keyboard.dismiss(); latest.current.onBack(event); }
    },
    onPanResponderTerminate: () => { rejected.current = true; },
    onPanResponderTerminationRequest: () => true,
  })).current;
  if (Platform.OS !== 'ios') return <>{children}</>;
  // This app switches screens rather than maintaining an interactive back stack.
  // Moving only the chat exposes the parent, then snaps back before navigation.
  // Keep the surface still and use the existing Back action once on release.
  return <BlockAncestorBack.Provider value={block}>
    <View testID="chat-edge-back" style={styles.fill}
      {...pan.panHandlers}>{children}</View>
  </BlockAncestorBack.Provider>;
}

const styles = StyleSheet.create({ fill: { flex: 1 } });
